import type {
  IseeSessionQuery,
  IseeSessionTree,
} from '@tronche2tek/shared-types';

import type { IseeQueryState } from './cards-url-state';

/**
 * The picker's state. The tree levels are raw <select> strings ('' = none,
 * or all campuses); the multi-selects narrowing the picked cohort are a list
 * of ticked values, null meaning all of them — the default.
 */
export interface IseeTreeFields {
  school: string;
  program: string;
  cohort: string;
  campus: string;
  admStatus: string[] | null;
  section: string[] | null;
  speciality: string[] | null;
}

/** The levels in tree order: picking one clears every level below it. */
export const TREE_LEVELS = ['school', 'program', 'cohort', 'campus'] as const;
export type TreeLevel = (typeof TREE_LEVELS)[number];

/** The multi-selects narrowing the picked cohort. */
export const NARROWERS = ['admStatus', 'section', 'speciality'] as const;
export type Narrower = (typeof NARROWERS)[number];

const NO_FIELDS: IseeTreeFields = {
  school: '',
  program: '',
  cohort: '',
  campus: '',
  admStatus: null,
  section: null,
  speciality: null,
};

/** A child of a tree node, or an empty node — never a prototype key. */
function child<T>(node: Record<string, T>, key: string): T | undefined {
  return Object.hasOwn(node, key) ? node[key] : undefined;
}

/** The values offered at each level, given what is picked above it. */
export function treeOptions(
  tree: IseeSessionTree,
  fields: IseeTreeFields,
): Record<TreeLevel, string[]> {
  const programs = child(tree, fields.school) ?? {};
  const cohorts = child(programs, fields.program) ?? {};
  const campuses = child(cohorts, fields.cohort) ?? {};
  return {
    school: Object.keys(tree),
    program: Object.keys(programs),
    cohort: Object.keys(cohorts),
    campus: Object.keys(campuses),
  };
}

/**
 * What the picked cohort (its campus, or all of them) can be narrowed to: its
 * admission statuses — already cut down to the allowed ones by the backend —
 * the sections of its sessions and its specialities. Nothing until a cohort
 * is picked.
 */
export function narrowerOptions(
  tree: IseeSessionTree,
  fields: IseeTreeFields,
): Record<Narrower, string[]> {
  const programs = child(tree, fields.school) ?? {};
  const campuses = child(child(programs, fields.program) ?? {}, fields.cohort);
  const leaves = !campuses
    ? []
    : fields.campus
      ? [child(campuses, fields.campus)]
      : Object.values(campuses);
  const distinct = (values: string[]) => [...new Set(values)].sort();
  return {
    admStatus: distinct(leaves.flatMap((leaf) => leaf?.admStatus ?? [])),
    section: distinct(
      leaves.flatMap((leaf) => leaf?.sessions.map((s) => s.section) ?? []),
    ),
    speciality: distinct(leaves.flatMap((leaf) => leaf?.speciality ?? [])),
  };
}

/** Pick a level's value; every level below it and the narrowers reset. */
export function pickLevel(
  fields: IseeTreeFields,
  level: TreeLevel,
  value: string,
): IseeTreeFields {
  const next = {
    ...fields,
    [level]: value,
    admStatus: null,
    section: null,
    speciality: null,
  };
  for (const below of TREE_LEVELS.slice(TREE_LEVELS.indexOf(level) + 1)) {
    next[below] = '';
  }
  return next;
}

/**
 * Tick or untick one value of a multi-select. Starting from "all", the
 * offered values are the list; ticking everything back is "all" again.
 */
export function toggleNarrower(
  selected: string[] | null,
  options: string[],
  value: string,
): string[] | null {
  const base = new Set(selected ?? options);
  const next = base.has(value)
    ? options.filter((o) => o !== value && base.has(o))
    : options.filter((o) => o === value || base.has(o));
  return next.length === options.length ? null : next;
}

/**
 * The picker's state read from a selection (the form's, restored from the URL),
 * down to the first level the tree no longer offers — a cohort whose sessions
 * have ended, a stale campus. A multi-select keeps the values still offered,
 * or falls back to all; '' is nothing ticked.
 */
export function initialTreeFields(
  tree: IseeSessionTree,
  initialQuery: IseeQueryState | undefined,
): IseeTreeFields {
  let fields = NO_FIELDS;
  for (const level of TREE_LEVELS) {
    const value = initialQuery?.[level];
    if (!value || !treeOptions(tree, fields)[level].includes(value)) {
      break;
    }
    fields = { ...fields, [level]: value };
  }
  const options = narrowerOptions(tree, fields);
  for (const narrower of NARROWERS) {
    const raw = initialQuery?.[narrower];
    const offered = new Set(options[narrower]);
    const kept =
      raw === '' ? [] : (raw?.split(',') ?? []).filter((v) => offered.has(v));
    if (raw === '' || (kept.length > 0 && kept.length < offered.size)) {
      fields = { ...fields, [narrower]: kept };
    }
  }
  return fields;
}

/**
 * A selection cut down to what the tree still offers — what the picker shows,
 * and so what may be submitted. Before the tree has loaded it is left as is.
 */
export function cleanQuery(
  tree: IseeSessionTree | null,
  query: IseeQueryState,
): IseeQueryState {
  return tree ? toQueryState(initialTreeFields(tree, query)) : query;
}

/**
 * The selection as the URL carries it: an unpicked level, or a multi-select
 * left at "all", leaves its key out. Nothing ticked is kept, as ''.
 */
export function toQueryState(fields: IseeTreeFields): IseeQueryState {
  const opt = (v: string) => (v === '' ? undefined : v);
  return {
    school: opt(fields.school),
    program: opt(fields.program),
    cohort: opt(fields.cohort),
    campus: opt(fields.campus),
    admStatus: fields.admStatus?.join(','),
    section: fields.section?.join(','),
    speciality: fields.speciality?.join(','),
  };
}

/**
 * The cohort as the API takes it (no campus = all of them), null until one is
 * picked — or while a multi-select has nothing ticked: no one to generate for.
 */
export function toSessionQuery(query: IseeQueryState): IseeSessionQuery | null {
  const { school, program, cohort, campus, admStatus, section, speciality } =
    query;
  if (
    !school ||
    !program ||
    !cohort ||
    admStatus === '' ||
    section === '' ||
    speciality === ''
  ) {
    return null;
  }
  return {
    school,
    program,
    cohort,
    ...(campus && { campus }),
    ...(admStatus && { admStatus }),
    ...(section && { section }),
    ...(speciality && { speciality }),
  };
}
